/*------------------------------------------------*/
/* LIBRARIES
/*------------------------------------------------*/
import React from 'react';
import PropTypes from 'prop-types';
import { Link, StaticQuery, graphql } from 'gatsby';
import _get from 'lodash/get';
/*------------------------------------------------*/
/* INTERNAL DEPENDENCIES
/*------------------------------------------------*/
// Components
import Logo from '../Logo';
import Container from '../Container';
import SocialBranding from '../SocialMedia/SocialBranding';
import DownloadAndroid from '../../../static/images/android.png';
import DownloadIos from '../../../static/images/ios.png';
// Styles
import {
  Wrapper,
  Line,
  LineWrapper,
  Menu,
  Body,
  MenusWrapper,
} from './FooterStyles';
// Helpers
import urlHelper from '../../utils/url';


const getMainMenu = (solutionItems, comparisonItems) => {
  const menus = [
    {
      name: 'Product',
      links: [
        { name: 'Features', url: '/product/' },
        { name: 'With Slack', url: '/slack/' },
        { name: 'With Webex', url: '/webex/' },
        { name: 'Extensions', url: '/extensions/' },
        { name: 'Pricing', url: '/pricing/' },
      ],
    },
    {
      name: 'Learn',
      links: [
        { name: 'Getting started with Workast', url: '/help/article/quick-start-guide-how-to-use-workast/' },
        { name: 'Creating a task in Slack', url: '/help/article/how-to-create-a-new-task-in-slack/' },
        { name: 'Creating a checklist in Slack', url: '/help/article/how-to-create-a-checklist-on-slack/' },
        { name: 'Creating tasks from your email', url: '/help/article/using-email-to-create-a-task/' },
        { name: 'Creating custom forms', url: '/help/article/creating-and-using-forms-in-workast/' },
        { name: 'Creating advanced reports', url: '/help/article/advanced-searches-and-reports/' },
        { name: 'Using custom fields for tasks', url: '/help/article/custom-fields-in-workast/' },
        { name: '2-way calendar sync', url: '/help/article/calendar-sync-extension/' },
      ],
    },
    {
      name: 'Resources',
      links: [
        { name: 'Templates Library', url: '/templates/' },
        { name: 'Free ebook', url: '/ebook/from-chaos-to-control-managing-your-business-with-workast/' },
        { name: 'Blog', url: '/blog/' },
        { name: 'Help Center', url: '/help/' },
        { name: 'API Docs', url: 'https://developers.workast.com/' },
        { name: 'Affiliate Program', url: '/affiliate-program/' },
        { name: 'Startup Program', url: '/startup-program/' },
      ],
    },
    {
      name: 'Blog',
      links: [
        { name: 'Product', url: '/blog/product/' },
        { name: 'Best practices', url: '/blog/best-practices/' },
        { name: 'Customer stories', url: '/blog/customer-stories/' },
        { name: 'Business Tips', url: '/blog/business-tips/' },
        { name: 'Android', url: 'http://l.workast.io/download-android', icon: <img src={DownloadAndroid} alt="Android" /> },
        { name: 'iOS', url: 'http://l.workast.io/download-ios', icon: <img src={DownloadIos} alt="iOS" /> },
      ],
    },
  ];

  const mainMenuUpdated = [...menus];

  const solutionOptions = solutionItems.map(item => ({
    name: _get(item, 'team', ''),
    url: urlHelper.getSolutionArticle(_get(item, 'slug', '')),
  }));
  solutionOptions.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : -1));
  // If there are "Solution" items, then we add it to the array:
  if (solutionOptions.length > 0) {
    mainMenuUpdated.splice(2, 0, {
      name: 'Solutions',
      links: solutionOptions,
    });
  }

  const comparisonOptions = comparisonItems.map(item => ({
    name: `Workast vs ${_get(item, 'competitor', '')}`,
    url: urlHelper.getComparisonArticle(_get(item, 'slug', '')),
  }));
  comparisonOptions.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : -1));
  // If there are "Comparison" items, then we add it to the array:
  if (comparisonOptions.length > 0) {
    mainMenuUpdated.splice(2, 0, {
      name: 'Compare',
      links: comparisonOptions,
    });
  }

  return mainMenuUpdated;
};


const StaticFooter = () => (
  <StaticQuery
    query={graphql`
      query MyQueryForFooter {
        allContentfulSolutionPage {
          nodes {
            slug
            team
          }
        }
        allContentfulComparisonPage {
          nodes {
            slug
            competitor
          }
        }
      }
    `}
    render={(data) => {
      const solutionItems = _get(data, 'allContentfulSolutionPage.nodes', []);
      const comparisonItems = _get(data, 'allContentfulComparisonPage.nodes', []);
      const menus = getMainMenu(solutionItems, comparisonItems);

      return (
        <Footer
          menus={menus}
        />
      );
    }}
  />
);


const Footer = (props) => {
  const { menus } = props;
  return (
    <Wrapper>
      <div className="dots-divider" />
      <Container>
        <Body>
          <MenusWrapper>
            {menus.map(menu => (
              <Menu key={menu.name}>
                <h3>{menu.name}</h3>
                <ul>
                  {menu.links.map(({ name, url, icon }) => {
                    const itemContent = icon || name;
                    const cssClassItem = icon ? 'with-image' : '';

                    return (
                      <li key={name} className={cssClassItem}>
                        {url.indexOf('http') >= 0
                          ? <a href={url} rel="noreferrer" target="_blank">{itemContent}</a>
                          : <Link to={url}>{itemContent}</Link>
                        }
                      </li>
                    );
                  })}
                </ul>
              </Menu>
            ))}
          </MenusWrapper>
        </Body>
      </Container>
      <LineWrapper>
        <Container>
          <Line>
            <div className="left-content">
              <div className="logo-container">
                <Logo size="30px" />
                <span className="workast-name">
                  Workast
                </span>
              </div>
              <div className="text-container">
                <span className="workast-name">
                  Workast
                </span>
                <div className="links-section">
                  {`© ${new Date().getFullYear()} `}
                  <Link to="/privacy-policy/">| Privacy Policy </Link>
                  <Link to="/terms-of-service/">| Terms of Service </Link>
                  <Link to="/security/">| Security Policy </Link>
                  <a href="https://status.workast.com/" rel="noreferrer" target="_blank">| System Status </a>
                </div>
              </div>
            </div>
            <div className="right-content">
              <SocialBranding list={['tiktok', 'instagram', 'youtube', 'twitter', 'linkedIn']} />
            </div>
          </Line>
        </Container>
      </LineWrapper>
    </Wrapper>
  );
};
Footer.propTypes = {
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      links: PropTypes.arrayOf(
        PropTypes.shape({

        })
      ),
    })
  ).isRequired,
};


/*------------------------------------------------*/
/* EXPORTS
/*------------------------------------------------*/
export default StaticFooter;
